import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { requestOtp, verifyOtp, clearError, logout, restoreAuth } from '../../store/slices/authSlice';
import { RootState, AppDispatch } from '../../store/store';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  Menu,
  IconButton,
} from '@mui/material';
import { AccountCircle, Logout } from '@mui/icons-material';
import OtpInput from './OtpInput';

interface ExtendedUser {
  mobileNumber: string;
  role: string;
  userId: number;
  name: string | null;
}

const Login: React.FC = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // Default to India
  const [otp, setOtp] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  
  const { 
    loading, 
    error, 
    otpSent, 
    mobileNumber: storedMobileNumber,
    isAuthenticated,
    user: baseUser
  } = useSelector((state: RootState) => state.auth);

  // Type assertion to handle extended user properties
  const user = baseUser as ExtendedUser | null;

  // Clear any errors when component mounts or unmounts and check localStorage
  useEffect(() => {
    // Check if user is already authenticated in localStorage
    const storedAuth = localStorage.getItem('auth');
    if (storedAuth && !isAuthenticated) {
      try {
        const authData = JSON.parse(storedAuth);
        if (authData.token && authData.user) {
          dispatch(restoreAuth({ user: authData.user, token: authData.token }));
        }
      } catch (error) {
        // If there's an error parsing localStorage, clear it
        localStorage.removeItem('auth');
      }
    }

    return () => {
      dispatch(clearError());
    };
  }, [dispatch, isAuthenticated]);

  const handleMobileNumberSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileNumber.trim().length === 10) {
      // Pass the country code with the mobile number
      // dispatch(requestOtp(`${countryCode}${mobileNumber}`));
      dispatch(requestOtp(`${mobileNumber}`));
    }
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.trim().length === 4 && storedMobileNumber) {
      dispatch(verifyOtp({ mobileNumber: storedMobileNumber, otp }));
    }
  };

  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setMobileNumber(value);
    }
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    setAnchorEl(null);
  };

  // OTP is now handled by the OtpInput component

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#121212',
        position: 'relative'
      }}
    >
      {/* User info in top-right corner */}
      {isAuthenticated && user && (
        <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
          <IconButton
            onClick={handleUserMenuClick}
            sx={{ color: 'white' }}
          >
            <AccountCircle />
            <Typography variant="body2" sx={{ ml: 1, color: 'white' }}>
              {user.name || user.mobileNumber}
            </Typography>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              {user.name && (
                <Typography variant="body2" color="textSecondary">
                  Name: {user.name}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                Mobile: {user.mobileNumber}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Role: {user.role}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                User ID: {user.userId}
              </Typography>
              <Button
                startIcon={<Logout />}
                onClick={handleLogout}
                sx={{ mt: 1 }}
                fullWidth
              >
                Logout
              </Button>
            </Box>
          </Menu>
        </Box>
      )}

      {/* Demo link to Trip Details */}
      {/* <Button
        component={Link}
        to="/trip-details"
        variant="contained"
        color="primary"
        sx={{ position: 'absolute', top: 20, right: 20 }}
      >
        View Trip Details Demo
      </Button> */}
      
      {!isAuthenticated && (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            backgroundColor: '#1e1e1e',
            color: 'white',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            ONO Operator
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          {!otpSent ? (
            <form onSubmit={handleMobileNumberSubmit}>
              <Typography variant="h6" gutterBottom>
                Login with Mobile Number
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, mb: 2 }}>
                {/* <Box sx={{ width: '100%' }}>
                  <FormControl fullWidth variant="outlined" sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiSelect-select': {
                      color: 'white',
                    },
                  }}>
                    <InputLabel id="country-code-label">Country Code</InputLabel>
                    <Select
                      labelId="country-code-label"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      label="Country Code"
                      disabled={loading}
                      renderValue={(value) => value === '+91' ? 'India (+91)' : 'USA (+1)'}
                      MenuProps={{ 
                        PaperProps: { sx: { maxHeight: 200 } },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        }
                      }}
                      sx={{ 
                        '& .MuiSelect-select': { 
                          paddingRight: '32px',
                          whiteSpace: 'normal',
                          overflow: 'visible'
                        }
                      }}
                    >
                      <MenuItem value="+91">India (+91)</MenuItem>
                      <MenuItem value="+1">USA (+1)</MenuItem>
                    </Select>
                  </FormControl>
                </Box> */}
                <Box sx={{ width: '100%' }}>
                  <TextField
                    label="Mobile Number"
                    variant="outlined"
                    fullWidth
                    value={mobileNumber}
                    onChange={handleMobileNumberChange}
                    placeholder="Enter 10 digit number"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                      },
                    }}
                  />
                </Box>
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading || mobileNumber.length !== 10}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Request OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <Typography variant="h6" gutterBottom>
                Enter OTP
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                OTP sent to {storedMobileNumber}. Please enter the 4-digit code.
              </Typography>
              
              {/* OTP Input with 4 separate boxes */}
              <OtpInput
                value={otp}
                onChange={setOtp}
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading || otp.length !== 4}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify OTP'}
              </Button>
              <Button
                variant="text"
                color="primary"
                fullWidth
                onClick={() => dispatch(requestOtp(storedMobileNumber || ''))}
                disabled={loading}
                sx={{ mt: 1 }}
              >
                Resend OTP
              </Button>
            </form>
          )}
        </Paper>
      )}

      {/* Show welcome message or dashboard when authenticated */}
      {isAuthenticated && (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 600,
            backgroundColor: '#1e1e1e',
            color: 'white',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Welcome to ONO Operator
          </Typography>
          <Typography variant="h6" align="center" color="primary">
            Hello, {user?.name || user?.mobileNumber}!
          </Typography>
          <Typography variant="body1" align="center" sx={{ mt: 2 }}>
            You are successfully logged in as an {user?.role}.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Login;
